/**
 * StepCard — Shared step layout component.
 *
 * Purely presentational. Renders the two-column card layout used by every
 * in-app onboarding step:
 *   Left  — step label, title, description, primary CTA button, skip link
 *   Right — image or video media area
 *
 * No step-specific logic lives here.
 */

import React from "react";
import {
  CtaButton,
  MediaArea,
  SkipLink,
  StepCardWrapper,
  StepDescription,
  StepLabel,
  StepMediaCol,
  StepTextCol,
  StepTitle,
  StepTitleGroup,
  StepTitleSubGroup,
  StepButtonsContainer,
  PlayButtonOverlay,
  PlayButtonWrapper,
  PlayIcon,
} from "../../shared/appOnboardingStyles";
import { useResponsive } from "../../../../customHooks/useResponsive";
import { getYouTubeVideoId, isYouTubeUrl } from "../../../../utils/helper";

const StepCard = ({
  stepLabel,
  title,
  description,
  ctaLabel,
  skipLabel = "Skip",
  onCtaClick,
  onSkip,
  mediaSrc,
  mediaType = "image",
  isCtaLoading = false,
  backgroundColor = "#f0f4ff",
  backgroundImage = null,
  videoUrl = null,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const isMobileView = isMobile || isTablet;
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
  };

  return (
    <StepCardWrapper>
      {/* Left column — text content */}
      <StepTextCol>
        <StepTitleGroup width={isMobileView ? "auto" : "614px"} >
          <StepTitleSubGroup>
            {stepLabel && <StepLabel>{stepLabel}</StepLabel>}
            <StepTitle>{title}</StepTitle>
          </StepTitleSubGroup>
          <StepDescription>{description}</StepDescription>
        </StepTitleGroup>

        <StepButtonsContainer>
          <CtaButton
            type="button"
            onClick={onCtaClick}
            disabled={isCtaLoading}
          >
            {isCtaLoading ? "Loading…" : ctaLabel}
          </CtaButton>

          {onSkip && (
            <SkipLink type="button" onClick={onSkip}>
              {skipLabel}
            </SkipLink>
          )}
        </StepButtonsContainer>
      </StepTextCol>

      {/* Right column — media */}
      <StepMediaCol>
        <MediaArea
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: backgroundColor,
          }}
        >
          {!isVideoPlaying ? (
            <>
              {backgroundImage && (
                <img
                  src={backgroundImage}
                  alt={title}
                  style={{
                    maxWidth: "80%",
                    maxHeight: "80%",
                    objectFit: "contain",
                  }}
                />
              )}
              {videoUrl && (
                <PlayButtonOverlay onClick={handlePlayVideo}>
                  <PlayButtonWrapper>
                    <PlayIcon />
                  </PlayButtonWrapper>
                </PlayButtonOverlay>
              )}
            </>
          ) : isYouTubeUrl(videoUrl) ? (
            <iframe
              title={title}
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(videoUrl)}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <video
              src={videoUrl}
              autoPlay
              controls
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              onEnded={() => setIsVideoPlaying(false)}
            />
          )}
        </MediaArea>
      </StepMediaCol>
    </StepCardWrapper>
  );
};

export default StepCard;
